import React, { useEffect } from 'react';
import { AddCircleOutlineTwoToneIcon } from 'tuui';
import { DeletableItem, INDICATION_TYPES } from 'taboola-ultimate-ui';
import OutlineEditableText from 'modules/campaigns/components/OutlineEditableText';
import { buttonTextValidations } from 'modules/codeless-events/validations/buttonValidations';
import { withIndication } from 'modules/errors';
import { GTM_EVENTS } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { useFormValidatedValue } from 'modules/taboola-common-frontend-modules/validations';
import styles from './codelessCombineSameTextButtonsInputs.module.scss';

const OutlineEditableTextWithIndication = withIndication(OutlineEditableText);

const ButtonTextInput = ({ index, value, placeholder, onChange, componentName, disabled, onValidationChange }) => {
    const { onChange: onValidatedChange, indicationData } = useFormValidatedValue({
        field: `trackedElements.${index}.displayText`,
        validations: buttonTextValidations,
        validationDependencies: { skipValidations: disabled },
    });

    const hasError = indicationData.indicationType === INDICATION_TYPES.ERROR;

    useEffect(() => {
        if (onValidationChange) {
            onValidationChange(index, hasError);
        }
    }, [index, hasError, onValidationChange]);

    const handleChange = val => {
        onValidatedChange(val);
        onChange(val, index);
    };

    return (
        <OutlineEditableTextWithIndication
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            className={styles['editable-text']}
            data-metrics-event-name={GTM_EVENTS.USABILITY}
            data-metrics-component={`${componentName}-input-${index}`}
            {...indicationData}
        />
    );
};

export const CodelessCombineSameTextButtonsInputs = ({
    values,
    placeholder,
    disabled,
    onChange,
    onAdd,
    onRemove,
    componentName,
    onValidationChange,
}) => {
    if (disabled) {
        return (
            <div className={styles['multi-text-disabled']}>
                <OutlineEditableText
                    placeholder={placeholder}
                    disabled={true}
                    value=""
                    className={styles['editable-text-disabled']}
                />
            </div>
        );
    }

    return (
        <div className={styles['multi-text-container']}>
            {values.map((value, index) => (
                <div key={index}>
                    <div className={styles['text-input-row']}>
                        <ButtonTextInput
                            index={index}
                            value={value}
                            placeholder={placeholder}
                            onChange={onChange}
                            componentName={componentName}
                            disabled={disabled}
                            onValidationChange={onValidationChange}
                        />
                    </div>

                    {index < values.length - 1 && (
                        <div className={styles['or-separator']}>
                            <div className={styles['or-content']}>
                                <div className={styles['or-text']}>
                                    <FormattedMessage id="codeless.conversion.global.setup.or" defaultMessage="OR" />
                                </div>
                                <div className={styles['description-text']}>
                                    <FormattedMessage
                                        id="codeless.conversion.global.setup.multi.text.description"
                                        defaultMessage="Event triggers if any of these text match."
                                    />
                                </div>
                            </div>
                            {values.length > 1 && (
                                <DeletableItem
                                    onDelete={() => onRemove(index + 1)}
                                    isItemDeletable={true}
                                    buttonProps={{
                                        'aria-label': 'Remove text input below',
                                        className: styles['remove-button'],
                                    }}
                                />
                            )}
                        </div>
                    )}
                </div>
            ))}

            <div className={styles['add-button-section']}>
                <div role="button" onClick={onAdd} className={styles['add-or-button']} aria-label="Add text input">
                    <AddCircleOutlineTwoToneIcon fontSize="small" />
                    <FormattedMessage
                        id="codeless.conversion.global.setup.add.text_match"
                        defaultMessage="Add another text match"
                    />
                </div>
            </div>
        </div>
    );
};
